import {
  StyledTableCell,
  StyledTableSortLabel,
} from '../../components/TableTheme';
import { OrderEnum, SortEnum } from '../../types/pagination.types';
import { UserRole } from '../../types/user.types';
import { useIsUserRole } from '../../util/useIsUserRole';
import { useTable } from '../../util/useTable';
import {
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  LinearProgress,
  Stack,
  TablePagination,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Download,
  LockOutlined,
  SaveOutlined,
  UndoOutlined,
  UploadFile,
} from '@mui/icons-material';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import * as React from 'react';
import { Trash, PlusCircle } from 'react-feather';
import { useConfirm } from 'material-ui-confirm';
import { CSVLink } from 'react-csv';
import { formMsg, notifyMsg } from '../../configs';
import { FC, useEffect, useRef, useState } from 'react';
import { displayDate } from '../../util/dateTime';
import {
  useAddKeyValues,
  useDeleteKvsKeyValue,
  useGetKeyValueListOfaGroup,
  useGetKvsGroup,
  useUpdateKeyValue,
} from '../../api-http/kvs';
import { AlertBar } from '../../components/AlertBar';
import { useSnack } from '../../util/useSnack';
import { managementTableSx } from '../../components/ManagementPageLayout';
import { DialogModal } from '../../components';
import { ImportKvsCsvForm } from './ImportKvsCsvForm';

const actionButtonSx = {
  width: 32,
  height: 32,
  border: '1px solid #fecaca',
  borderRadius: 2,
  color: '#dc2626',
  bgcolor: '#fffafa',
};

const KEY_PATTERN = /^[0-9A-Z_]+$/;

interface KvsKeyValueTableProps {
  searchText: string;
  kvsGroupId: string;
  kvsGroupName: string;
}

interface DraftKeyValue {
  key: string;
  value: string;
}

interface NewRow {
  tempId: string;
  key: string;
  value: string;
}

const KvsKeyValuesTable: FC<KvsKeyValueTableProps> = ({
  searchText,
  kvsGroupId,
  kvsGroupName,
}) => {
  const confirm = useConfirm();
  const { sortOnClick, pagingParams, tableProps } = useTable(
    false,
    SortEnum.key,
  );
  const { sortby, order } = pagingParams;
  const { showErrSnack, showSuccessSnack } = useSnack();
  const [draftValues, setDraftValues] = useState<Record<string, DraftKeyValue>>(
    {},
  );
  const [newRows, setNewRows] = useState<NewRow[]>([]);
  const [importOpen, setImportOpen] = useState(false);
  const newRowCounter = useRef(0);

  const isViewOnly = useIsUserRole([UserRole.user, UserRole.layoutEditor]);
  const disableAddNew = useIsUserRole([
    UserRole.user,
    UserRole.layoutEditor,
    UserRole.dataEditor,
  ]);

  const {
    data: kvsGroupData,
    isLoading,
    error,
    refetch,
  } = useGetKvsGroup({
    ...pagingParams,
    search: searchText,
    kvsGroupId: kvsGroupId,
  });

  const {
    data: allKvsData,
    isLoading: isLoadingAllKvsData,
    refetch: refetchAllKvsData,
  } = useGetKeyValueListOfaGroup({ kvsGroupId });

  const { error: deleteError, mutate } = useDeleteKvsKeyValue({
    onSuccess: () => {
      showSuccessSnack(notifyMsg.KVS_KEY_VALUE_REMOVE_SUCCESS);
      refetch();
      refetchAllKvsData();
    },
    onError: (error) =>
      showErrSnack(error || notifyMsg.KVS_KEY_VALUE_REMOVE_FAILED),
  });

  const {
    error: updateError,
    isLoading: isSaving,
    mutateAsync: updateKeyValue,
  } = useUpdateKeyValue({
    onError: (error) =>
      showErrSnack(error || notifyMsg.KVS_KEY_VALUE_UPDATE_FAILED),
  });

  const {
    error: addError,
    isLoading: isAdding,
    mutateAsync: addKeyValues,
  } = useAddKeyValues({
    onError: (error) =>
      showErrSnack(error || notifyMsg.KVS_KEY_VALUE_ADD_FAILED),
  });

  const rows = kvsGroupData?.data || [];
  const allKeyValues = allKvsData?.data || [];
  const existingKeys = new Set(allKeyValues.map(({ key }) => key));
  const csvExportData = allKeyValues.map(({ key, value }) => ({ key, value }));

  const resetDrafts = () => {
    setDraftValues(
      rows.reduce<Record<string, DraftKeyValue>>((drafts, row) => {
        drafts[row._id] = { key: row.key, value: row.value };
        return drafts;
      }, {}),
    );
  };

  useEffect(() => {
    resetDrafts();
  }, [kvsGroupData?.data]);

  const onDeleteRecord = async (id: string) => {
    try {
      await confirm({
        description: notifyMsg.DELETE_CONFIRMATION,
        confirmationText: 'Yes',
      });
      mutate({ id });
    } catch (e) {}
  };

  const updateDraftValue = (id: string, value: string) => {
    setDraftValues((current) => ({
      ...current,
      [id]: {
        ...(current[id] || { key: '', value: '' }),
        value,
      },
    }));
  };

  const changedRows = rows.filter((row) => {
    const draft = draftValues[row._id];
    return draft && draft.value !== row.value;
  });

  const hasValidationErrors = rows.some((row) => {
    const draft = draftValues[row._id];
    return !draft || !draft.value.trim();
  });

  const addNewRow = () => {
    newRowCounter.current += 1;
    setNewRows((current) => [
      ...current,
      { tempId: `new-${newRowCounter.current}`, key: '', value: '' },
    ]);
  };

  const removeNewRow = (tempId: string) => {
    setNewRows((current) => current.filter((row) => row.tempId !== tempId));
  };

  const updateNewRowKey = (tempId: string, key: string) => {
    setNewRows((current) =>
      current.map((row) =>
        row.tempId === tempId ? { ...row, key: key.trim().toUpperCase() } : row,
      ),
    );
  };

  const updateNewRowValue = (tempId: string, value: string) => {
    setNewRows((current) =>
      current.map((row) => (row.tempId === tempId ? { ...row, value } : row)),
    );
  };

  const includedNewRows = newRows.filter(
    (row) => row.key.trim() || row.value.trim(),
  );

  const newRowErrors: Record<string, string | undefined> = {};
  const seenNewKeys = new Set<string>();
  includedNewRows.forEach((row) => {
    const key = row.key.trim();
    const value = row.value.trim();
    let rowError: string | undefined;
    if (!key) {
      rowError = formMsg.KEY_REQUIRED;
    } else if (!KEY_PATTERN.test(key)) {
      rowError = formMsg.INVALID_KEY;
    } else if (seenNewKeys.has(key)) {
      rowError = 'Duplicate key';
    } else if (existingKeys.has(key)) {
      rowError = 'Key already exists';
    } else if (!value) {
      rowError = formMsg.VALUE_REQUIRED;
    }
    if (!rowError) {
      seenNewKeys.add(key);
    }
    newRowErrors[row.tempId] = rowError;
  });

  const validNewRows = includedNewRows.filter(
    (row) => !newRowErrors[row.tempId],
  );
  const hasNewRowErrors = includedNewRows.some(
    (row) => newRowErrors[row.tempId],
  );

  const hasChanges = changedRows.length > 0 || includedNewRows.length > 0;
  const hasErrors = hasValidationErrors || hasNewRowErrors;

  const discardChanges = () => {
    resetDrafts();
    setNewRows([]);
  };

  const refetchAll = () => {
    refetch();
    refetchAllKvsData();
  };

  const saveChanges = async () => {
    if (!hasChanges || hasErrors) return;

    try {
      const requests: Promise<unknown>[] = changedRows.map((row) =>
        updateKeyValue({
          _id: row._id,
          key: row.key,
          value: draftValues[row._id].value,
        }),
      );

      if (validNewRows.length) {
        requests.push(
          addKeyValues({
            kvsGroup: kvsGroupId,
            kvsData: validNewRows.map(({ key, value }) => ({ key, value })),
          }),
        );
      }

      await Promise.all(requests);
      showSuccessSnack(notifyMsg.KVS_KEY_VALUE_UPDATE_SUCCESS);
      setNewRows([]);
      await refetchAll();
    } catch (e) {}
  };

  return (
    <>
      {(isLoading || isSaving || isAdding) && <LinearProgress />}
      <AlertBar
        severity="error"
        msg={
          error?.msg || deleteError?.msg || updateError?.msg || addError?.msg
        }
      />
      <Stack
        component={Paper}
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        spacing={1.5}
        sx={{ mb: 2, px: 2, py: 1.5, borderRadius: 2 }}
      >
        <Typography sx={{ color: '#64748b', fontSize: 14 }}>
          {isViewOnly
            ? 'Export key values to CSV.'
            : 'Keys are locked. Edit values directly, then save all changes together.'}
        </Typography>
        <Stack
          direction="row"
          spacing={1}
          justifyContent="flex-end"
          flexWrap="wrap"
          useFlexGap
        >
          <CSVLink
            data={csvExportData}
            headers={[
              { label: 'Key', key: 'key' },
              { label: 'Value', key: 'value' },
            ]}
            filename={`${kvsGroupName}.csv`}
            style={{
              textDecoration: 'none',
              pointerEvents:
                isLoadingAllKvsData || !csvExportData.length ? 'none' : 'auto',
            }}
          >
            <Button
              variant="outlined"
              startIcon={<Download fontSize="small" />}
              disabled={isLoadingAllKvsData || !csvExportData.length}
              sx={{ borderRadius: 2, textTransform: 'none' }}
            >
              Export CSV
            </Button>
          </CSVLink>
          {!isViewOnly && (
            <>
              <Button
                variant="outlined"
                startIcon={<UploadFile fontSize="small" />}
                onClick={() => setImportOpen(true)}
                disabled={disableAddNew}
                sx={{ borderRadius: 2, textTransform: 'none' }}
              >
                Import CSV
              </Button>
              <Button
                variant="outlined"
                startIcon={<UndoOutlined />}
                onClick={discardChanges}
                disabled={!hasChanges || isSaving || isAdding}
                sx={{ borderRadius: 2, textTransform: 'none' }}
              >
                Discard
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveOutlined />}
                onClick={saveChanges}
                disabled={!hasChanges || hasErrors || isSaving || isAdding}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  bgcolor: '#0b8fca',
                  fontWeight: 700,
                }}
              >
                Save changes
              </Button>
            </>
          )}
        </Stack>
      </Stack>
      <TableContainer
        component={Paper}
        sx={{
          ...managementTableSx,
          '& .MuiTableCell-body': {
            color: '#334155',
            borderColor: '#e8edf4',
            py: 1,
          },
        }}
      >
        <Table sx={{ minWidth: 900 }} aria-label="KVS key values">
          <TableHead>
            <TableRow>
              <StyledTableCell>
                <StyledTableSortLabel
                  active={sortby === SortEnum.key}
                  direction={sortby === SortEnum.key ? order : OrderEnum.asc}
                  onClick={() => sortOnClick(SortEnum.key)}
                >
                  Key
                </StyledTableSortLabel>
              </StyledTableCell>
              <StyledTableCell>Value</StyledTableCell>
              <StyledTableCell align="right">Updated By</StyledTableCell>
              <StyledTableCell align="right">
                <StyledTableSortLabel
                  active={sortby === SortEnum.updatedAt}
                  direction={
                    sortby === SortEnum.updatedAt ? order : OrderEnum.asc
                  }
                  onClick={() => sortOnClick(SortEnum.updatedAt)}
                >
                  Last updated
                </StyledTableSortLabel>
              </StyledTableCell>
              <StyledTableCell align="right">Used</StyledTableCell>
              <StyledTableCell align="center">Actions</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!rows.length && !newRows.length && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  {isLoading ? <CircularProgress /> : 'No Records Found '}
                </TableCell>
              </TableRow>
            )}
            {rows.map((keyValue) => {
              const draft = draftValues[keyValue._id] || {
                key: keyValue.key,
                value: keyValue.value,
              };
              const invalidValue = !draft.value.trim();

              return (
                <TableRow
                  key={keyValue._id}
                  sx={{
                    height: 56,
                    '&:last-child td, &:last-child th': { border: 0 },
                  }}
                >
                  <TableCell component="th" scope="row" sx={{ minWidth: 220 }}>
                    <TextField
                      fullWidth
                      size="small"
                      value={keyValue.key}
                      inputProps={{ 'aria-label': `Key ${keyValue.key}` }}
                      InputProps={{
                        readOnly: true,
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockOutlined sx={{ fontSize: 18 }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ minWidth: 280 }}>
                    <TextField
                      fullWidth
                      size="small"
                      value={draft.value}
                      disabled={isViewOnly || isSaving}
                      error={invalidValue}
                      helperText={
                        invalidValue ? 'Value is required' : undefined
                      }
                      onChange={(event) =>
                        updateDraftValue(keyValue._id, event.target.value)
                      }
                      inputProps={{ 'aria-label': `Value for ${keyValue.key}` }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    {keyValue.updatedBy || '-'}
                  </TableCell>
                  <TableCell align="right">
                    {displayDate(keyValue.updatedAt)}
                  </TableCell>
                  <TableCell align="right">
                    {keyValue.slides.length ? 'Yes' : 'No'}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip
                      title={
                        !isViewOnly && keyValue.slides.length
                          ? 'Used in slides'
                          : 'Delete key value'
                      }
                    >
                      <span>
                        <IconButton
                          aria-label={`Delete ${keyValue.key}`}
                          disabled={
                            isViewOnly || Boolean(keyValue.slides.length)
                          }
                          onClick={() => onDeleteRecord(keyValue._id)}
                          sx={actionButtonSx}
                        >
                          <Trash size={15} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
            {newRows.map((row) => {
              const rowError = newRowErrors[row.tempId];
              const keyTouched = row.key.trim() || row.value.trim();

              return (
                <TableRow
                  key={row.tempId}
                  sx={{
                    height: 56,
                    bgcolor: '#f8fbff',
                  }}
                >
                  <TableCell sx={{ minWidth: 220 }}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="KEY_NAME"
                      value={row.key}
                      error={Boolean(keyTouched && rowError)}
                      onChange={(event) =>
                        updateNewRowKey(row.tempId, event.target.value)
                      }
                      inputProps={{ 'aria-label': 'New key' }}
                    />
                  </TableCell>
                  <TableCell sx={{ minWidth: 280 }}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Value"
                      value={row.value}
                      error={Boolean(keyTouched && rowError)}
                      helperText={keyTouched ? rowError : undefined}
                      onChange={(event) =>
                        updateNewRowValue(row.tempId, event.target.value)
                      }
                      inputProps={{ 'aria-label': 'New value' }}
                    />
                  </TableCell>
                  <TableCell align="center">-</TableCell>
                  <TableCell align="right">-</TableCell>
                  <TableCell align="right">No</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Remove row">
                      <IconButton
                        aria-label="Remove new row"
                        onClick={() => removeNewRow(row.tempId)}
                        sx={actionButtonSx}
                      >
                        <Trash size={15} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
            {!isViewOnly && !disableAddNew && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Button
                    size="small"
                    startIcon={<PlusCircle size={16} />}
                    onClick={addNewRow}
                    sx={{ textTransform: 'none' }}
                  >
                    Add row
                  </Button>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          {...tableProps}
          rowsPerPageOptions={[15, 25, 35, 50, 100]}
          component="div"
          hidden={kvsGroupData?.pagination.total === 0}
          count={kvsGroupData?.pagination.total || 0}
        />
      </TableContainer>
      <DialogModal
        title="Import key values from CSV"
        onClose={() => setImportOpen(false)}
        open={importOpen}
        width={640}
      >
        <ImportKvsCsvForm
          onFinished={() => setImportOpen(false)}
          kvsGroup={kvsGroupId}
          existingKeys={Array.from(existingKeys)}
          onRefetchData={refetchAll}
        />
      </DialogModal>
    </>
  );
};

export default KvsKeyValuesTable;
